import * as ScrollArea from '@radix-ui/react-scroll-area';
import classNames from 'classnames';
import { ChevronDown, ChevronLeft, ChevronRight, Download, Search, Trash2, X } from 'lucide-react';
import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { GlobalContext } from '../context/GlobalContext';
import useFetch, { API, TableRequest } from '../hooks/api';
import { DateRangePicker } from '../shadcn/DateRangePicker';
import { Dialog } from './Dialog';
import { ConfirmDialog } from './ConfirmDialog';
import Select from './Select';
import Spinner, { ContainerSpinner } from './Spinner';
import { Button } from '../shadcn/Button';
import Input from './Input';
import Combobox from './Combobox';
import { ucfirst } from '../utils/formatters';

type SearchByOption = {
	column: string;
	placeholder: string;
	type?: 'numeric' | 'alphanumeric' | 'numbers_only' | 'numbers_letters_dot_comma' | 'combobox' | 'multi_combobox';
	label?: string;
	options?: { label: string; value: string }[];
};

export default function Table({
	userEmail,
	endpoint,
	orderBy,
	pagination,
	filters,
	search_by,
	filter_by,
	hideDateFilters = false,
	hideExport = false,
	exportLimit = 10000,
	allowExportWithoutTotal = false,
	hidePagination = false,
	recordCountLabel = undefined,
	recordCountDescription = undefined,
	actionButton = undefined,
	deleteButton = undefined,
	selectable = undefined,
	truncateColumns = undefined,
	firstColumns = undefined,
	cellRenderers = undefined,
	className = '',
	onDateRangeChange = undefined,
	onFilterChange = undefined,
	fillHeight = false
}: {
	userEmail: string | null;
	endpoint: keyof API;
	orderBy: { columnName: string; columnOrder: 'ASC' | 'DESC' } | null;
	pagination?: { batchSizes: number[]; default: number };
	filters?: {
		column: string;
		options: { label: string; value: string | boolean }[];
		defaultValue: string | boolean;
	}[];
	search_by?: SearchByOption | SearchByOption[];
	filter_by?: { column_name: string; value: string | boolean }[];
	hideDateFilters?: boolean;
	hideExport?: boolean;
	exportLimit?: number;
	allowExportWithoutTotal?: boolean;
	hidePagination?: boolean;
	recordCountLabel?: string;
	recordCountDescription?: string;
	actionButton?: {
		label: string;
		onClick: (row: { [key: string]: string | number }) => any;
	};
	deleteButton?: {
		onClick: (row: { [key: string]: string | number }) => Promise<true | string>;
	};
	selectable?: {
		idColumn: string;
		onSelectionChange: (selectedRows: { [key: string]: string | number }[]) => void;
		onAllPageSelected?: (allSelected: boolean) => void;
	};
	truncateColumns?: Record<string, number>;
	firstColumns?: string[];
	cellRenderers?: Record<string, (value: unknown) => ReactNode>;
	className?: string;
	onDateRangeChange?: (from: string | null, to: string | null) => void;
	onFilterChange?: (column: string, value: string | boolean | null) => void;
	fillHeight?: boolean;
}) {
	const { userData, internal } = useContext(GlobalContext);
	const { master_user, sub_stores, store_name } = userData!;

	const filterBy = [];
	if (!master_user && !sub_stores && store_name && !internal) filterBy.push({ column_name: 'Sucursal', value: store_name });
	if (filter_by) filterBy.push(...filter_by);

	const { data, error, loading, setPayload, payload } = useFetch(
		endpoint as 'https://api.krece.app/tables/payments/',
		'POST',
		true,
		{
			master_store_email: userEmail,
			last_object_id: null,
			...(pagination && { batch_size: pagination.default }),
			...(orderBy && { order_by: { column_name: orderBy.columnName, column_order: orderBy.columnOrder } }),
			...(filterBy.length ? { filter_by: filterBy } : null)
		}
	);

	const page = useRef(1);
	// pageStartCursors[i] holds the last_object_id needed to fetch page (i + 1); index 0 is page 1 (no cursor).
	// Cursors are recorded in the "next" handler from live data — never during a render (which can hold stale data while loading).
	const pageStartCursors = useRef<(string | null)[]>([null]);

	const total = useRef<number | null>(null);
	if (data?.total_count !== undefined && (data.total_count !== null || payload?.last_object_id === null)) {
		total.current = data.total_count;
	}

	const batchSize = useRef(pagination?.default || 100);
	const showBatchSizeSelector = pagination && pagination.batchSizes.length > 1;

	const dateRangeKey = useRef(Math.random());

	const [exportDataPayload, setExportDataPayload] = useState<null | TableRequest>(null);
	const [rowToDelete, setRowToDelete] = useState<{ [key: string]: string | number } | null>(null);
	const [selectedRowIndices, setSelectedRowIndices] = useState<Set<number>>(new Set());
	const exportLimitLabel = exportLimit.toLocaleString('es-VE');

	// Normalize search_by to always be an array for internal handling
	const searchByOptions = search_by ? (Array.isArray(search_by) ? search_by : [search_by]) : [];
	const [selectedSearchIndex, setSelectedSearchIndex] = useState(0);
	const [multiSearchValues, setMultiSearchValues] = useState<Record<string, string[]>>({});
	const [textSearchValues, setTextSearchValues] = useState<Record<string, string>>({});
	const currentSearchBy = searchByOptions[selectedSearchIndex];

	const applyTextSearch = (column: string, value: string) => {
		pageStartCursors.current = [null];
		page.current = 1;
		setPayload((payload) => {
			if (!value.trim() && 'search_by' in payload!) delete payload.search_by;

			return {
				...payload!,
				last_object_id: null,
				...(value.trim() && { search_by: { column_name: column, value: value.trim() } })
			} satisfies TableRequest;
		});
	};

	// Reorder columns if firstColumns is specified
	const tableData = (() => {
		if (!data?.column_names || !firstColumns?.length) return data;
		const order = [
			...firstColumns.filter((c) => data.column_names.includes(c)),
			...data.column_names.filter((c: string) => !firstColumns.includes(c)),
		];
		const indexMap = order.map((col: string) => data.column_names.indexOf(col));
		return {
			...data,
			column_names: order,
			rows: data.rows.map((row: any[]) => indexMap.map((i: number) => row[i])),
		};
	})();

	const recordCountText = (() => {
		if (!recordCountLabel || !tableData) return null;
		if (loading) return 'Actualizando registros...';
		if (total.current === null) {
			return `Página ${page.current} · ${tableData.rows.length.toLocaleString('es-VE')} en esta página · total no disponible`;
		}
		return `Página ${page.current} · ${total.current.toLocaleString('es-VE')} ${recordCountLabel}`;
	})();

	// Scroll to top of table when data changes
	const scrollAreaRef = useRef<null | HTMLDivElement>(null);
	useEffect(() => {
		if (scrollAreaRef.current) scrollAreaRef.current.scrollTop = 0;
	}, [payload]);

	// Reset selection when data changes
	useEffect(() => {
		setSelectedRowIndices(new Set());
		selectable?.onSelectionChange([]);
		selectable?.onAllPageSelected?.(false);
	}, [data]);

	return (
		<>
			{!data && !error && (
				<div className={classNames('flex w-full flex-col gap-4', { 'h-full': !fillHeight }, className)}>
					<div className="flex items-center justify-between">
						<div className="flex gap-2">
							<div className="h-10 w-36 animate-pulse rounded-lg bg-neutral-800/50" />
							<div className="h-10 w-44 animate-pulse rounded-lg bg-neutral-800/50" />
						</div>
						<div className="flex gap-2">
							<div className="h-10 w-48 animate-pulse rounded-lg bg-neutral-800/50" />
							<div className="h-10 w-32 animate-pulse rounded-lg bg-neutral-800/50" />
							<div className="size-10 animate-pulse rounded-lg bg-neutral-800/50" />
						</div>
					</div>
					<div className="flex-1 overflow-hidden rounded-lg border-[1.5px] border-neutral-900">
						<div className="flex h-16 items-center gap-6 bg-neutral-950 px-4">
							{[1, 2, 3, 4, 5, 6].map((i) => (
								<div key={i} className="h-4 w-24 animate-pulse rounded bg-neutral-800/50" />
							))}
						</div>
						{[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
							<div key={i} className="flex items-center gap-6 border-b border-neutral-900 px-4 py-4">
								{[1, 2, 3, 4, 5, 6].map((j) => (
									<div key={j} className="h-4 w-20 animate-pulse rounded bg-neutral-800/40" style={{ width: `${60 + (j * 13) % 40}px` }} />
								))}
							</div>
						))}
					</div>
					<div className="flex items-center justify-end gap-4">
						<div className="h-4 w-40 animate-pulse rounded bg-neutral-800/40" />
						<div className="flex gap-2">
							<div className="size-10 animate-pulse rounded-lg bg-neutral-800/40" />
							<div className="size-10 animate-pulse rounded-lg bg-neutral-800/40" />
						</div>
					</div>
				</div>
			)}
			{!data && error && (
				<div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
					<p className="text-lg text-neutral-300">
						No se encontraron datos para mostrar en la tabla. Es posible que la tabla esté vacía o que no exista.
					</p>
					<div className="flex gap-4">
						<button
							onClick={() => window.location.reload()}
							className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-80"
						>
							Recargar tabla
						</button>
						<button
							onClick={() => (window.location.href = '/')}
							className="rounded-lg bg-neutral-800 px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-80"
						>
							Ir al inicio
						</button>
					</div>
				</div>
			)}
			{data && tableData && (
				<div className={classNames('flex w-full flex-col gap-4 text-xs md:text-base', { 'h-full': !fillHeight }, className)}>
					{exportDataPayload && (
						<ExportData onClose={() => setExportDataPayload(null)} endpoint={endpoint} payload={exportDataPayload} />
					)}
					{rowToDelete && deleteButton && (
						<ConfirmDialog
							title="¿Estás seguro?"
							message="Esta acción no se puede deshacer."
							onConfirm={() => deleteButton.onClick(rowToDelete)}
							onClose={() => setRowToDelete(null)}
						/>
					)}
					{error && <Dialog title="¡Error!">Ha ocurrido un error cargando la tabla con los filtros seleccionados.</Dialog>}
					<div
						className={classNames('no-scrollbar flex min-h-10 w-full items-center justify-between gap-16 overflow-x-auto', {
							hidden: !filters?.length && hideDateFilters && hideExport
						})}
					>
						{filters?.length || searchByOptions.length ? (
							<>
								<div className="flex shrink-0 gap-2 md:gap-4">
									{filters?.map(
										(filter, idx) =>
											filter.options.length &&
											!(filter.options.length === 1 && filter.options.some((e) => e.value === filter.defaultValue)) && (
												<Select
													disabled={loading}
													className="bg-foreground md:bg-neutral-950"
													key={idx}
													options={filter.options}
													defaultValue={filter.defaultValue}
													onSelectedChange={(selected) => {
														pageStartCursors.current = [null];
														page.current = 1;
														total.current = null;
														const parsedValue = selected === null ? null : selected === 'true' ? true : selected === 'false' ? false : selected;
														onFilterChange?.(filter.column, parsedValue);
														setPayload((payload) => {
															const idx = payload?.filter_by?.findIndex((e) => e.column_name === filter.column);
															if (idx !== undefined && idx !== -1) payload?.filter_by?.splice(idx, 1);

															return {
																...payload!,
																last_object_id: null,
																filter_by: [
																	...(payload?.filter_by || []),
																	...(selected !== null
																		? [
																				{
																					column_name: filter.column,
																					value: parsedValue as string | boolean
																				}
																			]
																		: [])
																]
															} satisfies TableRequest;
														});
													}}
												/>
											)
									)}
								{currentSearchBy && (
									<>
										{searchByOptions.length > 1 && (
											<Select
												disabled={loading}
												className="bg-foreground md:bg-neutral-950"
												options={searchByOptions.map((opt, idx) => ({
													label: opt.label || opt.column,
													value: String(idx)
												}))}
												defaultValue="0"
												isDefaultValueNull={false}
												onSelectedChange={(selected) => {
													if (selected !== null) {
														setSelectedSearchIndex(Number(selected));
													}
												}}
											/>
										)}
										{currentSearchBy.type === 'combobox' && currentSearchBy.options ? (
											<Combobox
												disabled={loading}
												options={currentSearchBy.options}
												placeholder={currentSearchBy.placeholder}
												containerClassName="w-56"
												onSelectedChange={(value) => {
													pageStartCursors.current = [null];
													page.current = 1;
													setPayload((payload) => {
														if (!value && 'search_by' in payload!) delete payload.search_by;

														return {
															...payload!,
															last_object_id: null,
															...(value && {
																search_by: {
																	column_name: currentSearchBy.column,
																	value: String(value),
																},
															}),
														} satisfies TableRequest;
													});
												}}
											/>
										) : currentSearchBy.type === 'multi_combobox' && currentSearchBy.options ? (
											<div className="flex items-center gap-2">
												<Combobox
													key={`multi-${currentSearchBy.column}-${(multiSearchValues[currentSearchBy.column] || []).length}`}
													disabled={loading}
													options={currentSearchBy.options.filter((option) => !multiSearchValues[currentSearchBy.column]?.includes(String(option.value)))}
													placeholder={currentSearchBy.placeholder}
													containerClassName="w-56"
													onSelectedChange={(value) => {
														if (!value) return;
														const selectedValues = [...(multiSearchValues[currentSearchBy.column] || []), String(value)];
														pageStartCursors.current = [null];
														page.current = 1;
														setMultiSearchValues((values) => ({ ...values, [currentSearchBy.column]: selectedValues }));
														setPayload((payload) => ({
															...payload!,
															last_object_id: null,
															search_by: { column_name: currentSearchBy.column, value: selectedValues }
														}) satisfies TableRequest);
													}}
												/>
												{(multiSearchValues[currentSearchBy.column] || []).map((value) => (
													<button
														key={value}
														className="flex h-8 items-center gap-1 rounded-full bg-primary-600 px-2 text-xs font-medium text-primary-50"
														type="button"
														onClick={() => {
															const selectedValues = (multiSearchValues[currentSearchBy.column] || []).filter((selected) => selected !== value);
															pageStartCursors.current = [null];
															page.current = 1;
															setMultiSearchValues((values) => ({ ...values, [currentSearchBy.column]: selectedValues }));
															setPayload((payload) => {
																if (!selectedValues.length && 'search_by' in payload!) delete payload.search_by;
																return {
																	...payload!,
																	last_object_id: null,
																	...(selectedValues.length && { search_by: { column_name: currentSearchBy.column, value: selectedValues } })
																} satisfies TableRequest;
															});
														}}
													>
														{value}
														<X className="size-3" />
													</button>
												))}
											</div>
										) : (
											<div className="flex items-center gap-2">
												<Input
													key={`search-${selectedSearchIndex}-${currentSearchBy.column}`}
													disabled={loading}
													value={textSearchValues[currentSearchBy.column] || ''}
													onChange={(value) => setTextSearchValues((values) => ({ ...values, [currentSearchBy.column]: value }))}
													onSubmit={(value) => applyTextSearch(currentSearchBy.column, value)}
													Icon={<Search className="size-4" strokeWidth={2.5} />}
													allowedChars={currentSearchBy.type as 'numeric' | 'alphanumeric' | 'numbers_only' | 'numbers_letters_dot_comma' | undefined}
													placeholder={currentSearchBy.placeholder}
													className="w-56"
												/>
												<Button
													variant="secondary"
													className="!w-auto"
													disabled={loading}
													onClick={() => applyTextSearch(currentSearchBy.column, textSearchValues[currentSearchBy.column] || '')}
												>
													Buscar
												</Button>
											</div>
										)}
									</>
								)}
								</div>
							</>
						) : (
							<div />
						)}

						<div className="flex shrink-0 items-center gap-2 md:gap-4">
							{!hideDateFilters && (
								<>
									<DateRangePicker
										disabled={loading}
										className="rounded-lg bg-foreground md:bg-neutral-950"
										key={dateRangeKey.current}
										onRangeChange={(date_range) => {
											if (date_range?.from && date_range?.to) {
												const from = date_range.from!.toLocaleDateString('en-CA');
												const to = date_range.to!.toLocaleDateString('en-CA');
												pageStartCursors.current = [null];
												page.current = 1;
												setPayload(
													(payload) =>
														({
															...payload!,
															last_object_id: null,
															date_range: { from_date: from, to_date: to }
														}) satisfies TableRequest
												);
												onDateRangeChange?.(from, to);
											} else if (date_range === undefined) {
												pageStartCursors.current = [null];
												page.current = 1;
												setPayload(
													(payload) =>
														({
															...payload!,
															last_object_id: null,
															date_range: undefined
														}) satisfies TableRequest
												);
												onDateRangeChange?.(null, null);
											}
										}}
									/>
									<Select
										disabled={loading}
										className="bg-foreground md:bg-neutral-950"
										options={[
											'Ver todo',
											'Hoy',
											'Ayer',
											'Semana actual',
											'Última semana',
											'Este mes a la fecha',
											'Últimos 30 días'
										].map((e) => ({ label: e, value: e }))}
										defaultValue="Ver todo"
										onSelectedChange={(selected) => {
											dateRangeKey.current = Math.random();
											pageStartCursors.current = [null];
											page.current = 1;
											setPayload(
												(payload) =>
													({
														...payload!,
														last_object_id: null,
														...(selected !== null ? { date_range: { custom_range: selected as any } } : { date_range: undefined })
													}) satisfies TableRequest
											);
											onDateRangeChange?.(null, null);
										}}
									/>
								</>
							)}
							{!hideExport && (
								<button
									title={`Descargar hasta ${exportLimitLabel} resultados`}
									aria-label={`Descargar hasta ${exportLimitLabel} resultados`}
									data-disabled={
										(!allowExportWithoutTotal && !total.current) ||
										(allowExportWithoutTotal && !tableData.rows.length) ||
										(total.current !== null && total.current > exportLimit) ||
										loading
									}
									className={classNames(
										'flex size-10 shrink-0 items-center justify-center rounded-lg bg-foreground transition-colors hover:bg-primary-600 data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-60 data-[disabled=true]:hover:bg-background light:bg-neutral-900/70 md:bg-neutral-950',
										{ '!pointer-events-none': loading }
									)}
									onClick={() => {
										const hasResults = allowExportWithoutTotal ? tableData.rows.length > 0 : Boolean(total.current);
										if (!hasResults || (total.current !== null && total.current > exportLimit)) {
											const container = document.createElement('div');
											document.body.append(container);
											const root = createRoot(container);
											root.render(
												<Dialog title="¡Error!">
													{total.current !== null && total.current > exportLimit
														? `Se pueden exportar un máximo de ${exportLimitLabel} resultados. Actualmente hay ${total.current.toLocaleString('es-VE')}, favor reducir.`
														: 'No hay resultados para exportar.'}
												</Dialog>
											);
											return;
										}

										setExportDataPayload({
											...payload!,
											download_file: true
										} satisfies TableRequest);
									}}
								>
									<Download className="size-5" />
								</button>
							)}
						</div>
					</div>

					<ScrollArea.Root
						type="always"
						className={classNames('relative w-full overflow-hidden', fillHeight ? 'min-h-0 flex-1' : 'h-full min-h-[150px]')}
					>
						<ScrollArea.Viewport
							ref={scrollAreaRef}
							className="h-full w-full rounded-lg border-[1.5px] border-neutral-900 bg-foreground light:bg-background md:bg-[none]"
						>
							<article
								style={{ gridTemplateColumns: `${selectable ? '40px ' : ''}repeat(${tableData.column_names.length + (actionButton ? 1 : 0) + (deleteButton ? 1 : 0)},auto)` }}
								className="grid"
							>
								<ol className="contents select-none">
									{selectable && (
										<li className={classNames('sticky top-0 z-10 flex items-center justify-center bg-neutral-950', 'h-16')}>
											<input
												type="checkbox"
												className="size-4 cursor-pointer accent-primary-500"
												checked={tableData.rows.length > 0 && selectedRowIndices.size === tableData.rows.length}
												onChange={(e) => {
													const newSet = e.target.checked ? new Set(tableData.rows.map((_: any, i: number) => i)) : new Set<number>();
													setSelectedRowIndices(newSet);
													selectable.onSelectionChange(
														e.target.checked
															? tableData.rows.map((tr: any) =>
																	Object.fromEntries(tableData.column_names.map((name: string, i: number) => [name, tr[i]]))
																)
															: []
													);
													selectable.onAllPageSelected?.(e.target.checked && tableData.rows.length > 0);
												}}
											/>
										</li>
									)}
									{actionButton && (
										<li className={classNames('group pointer-events-none sticky top-0 z-10 flex min-w-max select-none items-center justify-between gap-1 bg-neutral-950 px-2 text-left font-medium leading-none text-neutral-400 transition-colors md:px-6', 'h-16')}>
											<span className="text-nowrap">{actionButton.label}</span>
										</li>
									)}
									{tableData.column_names.map((th, idx) => (
										<li
											key={idx}
											className={classNames(
												'group pointer-events-none sticky top-0 z-10 flex min-w-max select-none items-center justify-between gap-1 bg-neutral-950 px-2 text-left font-medium leading-none text-neutral-400 transition-colors light:bg-foreground md:px-6',
														'h-16',
												{
													'!pointer-events-auto cursor-pointer !bg-neutral-925 !text-neutral-100 light:!bg-neutral-900':
														orderBy && th === orderBy.columnName
												}
											)}
											onClick={() => {
												if (orderBy && th !== orderBy.columnName) return;

												pageStartCursors.current = [null];
												page.current = 1;
												setPayload(
													(payload) =>
														({
															...payload!,
															last_object_id: null,
															order_by: {
																column_name: th,
																column_order:
																	tableData.order_by.column_name !== th
																		? 'DESC'
																		: tableData.order_by.column_order === 'ASC'
																			? 'DESC'
																			: 'ASC'
															}
														}) satisfies TableRequest
												);
											}}
										>
											<span className="text-nowrap">{th === th?.toLowerCase() ? ucfirst(th) : th}</span>
											{orderBy && th === orderBy.columnName && (
												<ChevronDown
													className={classNames('size-4 shrink-0 text-neutral-300 transition-all group-hover:text-neutral-100', {
														'rotate-180': tableData.order_by.column_order === 'ASC'
													})}
													strokeWidth={2.5}
												/>
											)}
										</li>
									))}
									{deleteButton && (
										<li className={classNames('group pointer-events-none sticky top-0 z-10 flex min-w-max select-none items-center justify-between gap-1 bg-neutral-950 px-2 text-left font-medium leading-none text-neutral-400 transition-colors md:px-6', 'h-16')}>
											<span className="text-nowrap"></span>
										</li>
									)}
								</ol>
								{loading && (
									<div className="absolute z-20 flex h-[calc(100%-3px)] w-[calc(100%-2.5px)] items-center justify-center rounded-lg bg-black/50">
										<Spinner size={2.75} />
									</div>
								)}
								{tableData.rows.map((tr, rowIdx) => (
									<ol key={rowIdx} className="group contents [&>li]:border-b-[1.5px] [&>li]:border-neutral-900">
										{selectable && (
											<li
												className={classNames(
													'flex items-center justify-center transition-colors group-hover:bg-background',
													'py-4',
													{ '!border-b-0': tableData.rows.length - 1 === rowIdx }
												)}
											>
												<input
													type="checkbox"
													className="size-4 cursor-pointer accent-primary-500"
													checked={selectedRowIndices.has(rowIdx)}
													onChange={(e) => {
														const newSet = new Set(selectedRowIndices);
														if (e.target.checked) newSet.add(rowIdx);
														else newSet.delete(rowIdx);
														setSelectedRowIndices(newSet);
														selectable.onSelectionChange(
															Array.from(newSet).map((i) =>
																Object.fromEntries(tableData.column_names.map((name, ci) => [name, tableData.rows[i][ci]]))
															)
														);
														selectable.onAllPageSelected?.(newSet.size === tableData.rows.length && tableData.rows.length > 0);
													}}
												/>
											</li>
										)}
										{actionButton && (
											<li
												className={classNames(
													'relative min-w-max gap-[0.15rem] overflow-hidden text-ellipsis text-nowrap px-3 text-neutral-200 transition-colors group-hover:bg-background md:pr-[6vw]',
													'py-4',
													{ '!border-b-0': tableData.rows.length - 1 === rowIdx }
												)}
											>
												<Button
													className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
													onClick={() =>
														actionButton.onClick(Object.fromEntries(tableData.column_names.map((name, i) => [name, tr[i]])))
													}
												>
													{actionButton.label}
												</Button>
											</li>
										)}
										{tr.map((td, idx) => {
											const columnName = tableData.column_names[idx];
											const truncateWidth = truncateColumns?.[columnName];
											const cellRenderer = cellRenderers?.[columnName];
											return (
												<li
													key={idx}
													className={classNames(
														'min-w-max gap-[0.15rem] overflow-hidden text-ellipsis text-nowrap px-3 text-neutral-200 transition-colors group-hover:bg-background md:pr-[6vw]',
														'py-4',
														{ '!border-b-0': tableData.rows.length - 1 === rowIdx }
													)}
												>
													{cellRenderer ? (
														cellRenderer(td)
													) : truncateWidth ? (
														<div
															className="truncate"
															style={{ maxWidth: truncateWidth }}
															title={typeof td === 'string' ? td : undefined}
														>
															{td}
														</div>
													) : typeof td === 'string' && td.trim().match(/^https?:\/\//) ? (
														<a href={td} target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:underline">
															Abrir
														</a>
													) : (
														td
													)}
												</li>
											);
										})}
										{deleteButton && (
											<li
												className={classNames(
													'relative min-w-max gap-[0.15rem] overflow-hidden text-ellipsis text-nowrap px-3 text-neutral-200 transition-colors group-hover:bg-background md:pr-[6vw]',
													'py-4',
													{ '!border-b-0': tableData.rows.length - 1 === rowIdx }
												)}
											>
												<Button
													variant="ghost"
													className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-red-500 hover:text-red-600 hover:bg-red-500/10"
													onClick={() =>
														setRowToDelete(Object.fromEntries(tableData.column_names.map((name, i) => [name, tr[i]])))
													}
												>
													<Trash2 className="size-5" />
												</Button>
											</li>
										)}
									</ol>
								))}
								{!tableData.rows.length && (
									<div className="absolute top-8 z-10 flex h-[calc(100%-5px)] w-[calc(100%-5px)] select-none items-center justify-center text-lg text-neutral-300">
										No se encontraron resultados.
									</div>
								)}
							</article>
						</ScrollArea.Viewport>
						<ScrollArea.Scrollbar
							className="duration-[160ms] flex scale-x-[0.995] touch-none select-none ease-out data-[orientation=horizontal]:h-1.5 data-[orientation=vertical]:w-1.5 data-[orientation=horizontal]:flex-col"
							orientation="horizontal"
						>
							<ScrollArea.Thumb className="relative flex-1 cursor-pointer rounded-[10px] bg-neutral-800 transition-colors before:absolute before:left-1/2 before:top-1/2 before:h-full before:min-h-[44px] before:w-full before:min-w-[44px] before:-translate-x-1/2 before:-translate-y-1/2 before:content-[''] hover:bg-neutral-700" />
						</ScrollArea.Scrollbar>
						<ScrollArea.Scrollbar
							className="z-20 flex w-2.5 touch-none select-none rounded-full bg-neutral-950/60 p-0.5"
							orientation="vertical"
							style={{ top: '4rem', bottom: '0.375rem' }}
						>
							<ScrollArea.Thumb className="relative cursor-pointer rounded-full bg-neutral-600 transition-colors before:absolute before:left-1/2 before:top-1/2 before:h-full before:min-h-[44px] before:w-full before:min-w-[44px] before:-translate-x-1/2 before:-translate-y-1/2 before:content-[''] hover:bg-neutral-500" />
						</ScrollArea.Scrollbar>
					</ScrollArea.Root>
					{!hidePagination && (
						<div
							className={classNames('flex w-full items-center justify-end gap-4 text-[0.9rem] font-semibold tracking-tight', {
								'flex-wrap': recordCountLabel
							})}
						>
							{showBatchSizeSelector && (
								<Select
									disabled={loading}
									className="bg-foreground md:bg-neutral-950"
									options={pagination.batchSizes.map((size) => ({
										label: `${size} por página`,
										value: String(size),
									}))}
									defaultValue={String(pagination.default)}
									isDefaultValueNull={false}
									onSelectedChange={(selected) => {
										if (selected !== null) {
											const newSize = Number(selected);
											batchSize.current = newSize;
											pageStartCursors.current = [null];
											page.current = 1;
											setPayload(
												(payload) =>
													({
														...payload!,
														last_object_id: null,
														batch_size: newSize,
													}) satisfies TableRequest
											);
										}
									}}
								/>
							)}
							<p
								className="select-none text-base font-normal text-neutral-300"
								title={recordCountDescription}
								aria-live={recordCountLabel ? 'polite' : undefined}
							>
								{recordCountText ?? (pagination
									? total.current === null
										? `Página ${page.current}`
										: (page.current - 1) * batchSize.current - total.current > batchSize.current
											? page.current * batchSize.current
											: `${(page.current - 1) * batchSize.current} - ${total.current > batchSize.current ? page.current * batchSize.current : total.current} de ${total.current}`
									: `Mostrando ${total.current ?? 0} resultados`)}
							</p>

							{pagination && (
								<>
									<button
										disabled={page.current === 1 || loading}
										onClick={() => {
											page.current--;
											setPayload(
												(payload) =>
													({
														...payload!,
														last_object_id: pageStartCursors.current[page.current - 1] || null
													}) satisfies TableRequest
											);
										}}
										className="flex size-10 select-none items-center gap-1 rounded-lg bg-primary-600 px-3 py-2 transition-opacity hover:opacity-50 disabled:pointer-events-none disabled:bg-black disabled:opacity-30 light:bg-primary-500 light:text-neutral-1000 light:disabled:bg-neutral-900"
									>
										<ChevronLeft className="size-4 scale-110" strokeWidth={3} />
									</button>
									<button
										disabled={
											(total.current !== null
												? page.current * batchSize.current >= total.current
												: !data.last_object_id) || loading
										}
										onClick={() => {
											const nextStartCursor = data.last_object_id;
											page.current++;
											// data is current here (the button is disabled while loading), so record the real start cursor for this page.
											pageStartCursors.current[page.current - 1] = nextStartCursor;
											setPayload(
												(payload) =>
													({
														...payload!,
														last_object_id: nextStartCursor
													}) satisfies TableRequest
											);
										}}
										className="flex size-10 select-none items-center gap-1 rounded-lg bg-primary-600 px-3 py-2 transition-opacity hover:opacity-50 disabled:pointer-events-none disabled:bg-black disabled:opacity-30 light:bg-primary-500 light:text-neutral-1000 light:disabled:bg-neutral-900"
									>
										<ChevronRight className="size-4 scale-110" strokeWidth={3} />
									</button>
								</>
							)}
						</div>
					)}
				</div>
			)}
		</>
	);
}

function ExportData({ endpoint, payload, onClose }: { endpoint: keyof API; payload: TableRequest; onClose: () => any }) {
	const { data, error, loading } = useFetch(endpoint as 'https://api.krece.app/tables/payments/', 'POST', true, payload);

	return (
		<Dialog onClose={onClose} closable={!loading} title={loading ? 'Exportando...' : data?.download_link ? '¡Listo!' : '¡Error!'}>
			<div className="flex h-full w-full items-center justify-center">
				{loading ? (
					<div className="mb-0">
						<Spinner size={2} />
					</div>
				) : data?.download_link ? (
					<a
						href={data.download_link}
						target="_blank"
						rel="noreferrer"
						className="flex items-center justify-center gap-1 rounded-lg bg-primary-500/20 p-3 font-semibold text-primary-200 transition-colors hover:bg-primary-500/10 hover:text-primary-50 light:bg-primary-500 light:text-white"
					>
						<Download />
						Descargar
					</a>
				) : (
					error || 'Hubo un error generando el archivo.'
				)}
			</div>
		</Dialog>
	);
}
